/**
 * Humanised company name guessed from an email domain, used to pre-fill the
 * onboarding wizard (the user can always edit it).
 *
 *   jane@acme-labs.io        -> "Acme Labs"
 *   bob@mail.example.co.uk   -> "Example"
 *   sam@gmail.com            -> ""   (personal mailbox provider)
 */

/** Mailbox providers that never identify a company. */
export const PERSONAL_EMAIL_PROVIDERS = new Set([
    'gmail',
    'googlemail',
    'outlook',
    'hotmail',
    'live',
    'msn',
    'yahoo',
    'ymail',
    'icloud',
    'me',
    'mac',
    'aol',
    'proton',
    'protonmail',
    'pm',
    'zoho',
    'gmx',
    'yandex',
    'mail',
    'email',
    'fastmail',
    'hey',
    'tutanota',
    'tuta',
    'walla',
]);

/** Second-level labels that are part of a country TLD (`co.uk`, `com.au`, `co.il`). */
const SECOND_LEVEL_TLDS = new Set(['co', 'com', 'org', 'net', 'ac', 'gov', 'edu']);

const titleCase = (text) =>
    text
        .split(/\s+/)
        .filter(Boolean)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');

/**
 * Registrable label of a host name: strips the TLD (two labels for
 * `co.uk`-style suffixes) and any sub-domains.  Returns `''` for bare hosts.
 */
export function organizationLabelFromDomain(domain) {
    const labels = String(domain || '')
        .trim()
        .toLowerCase()
        .split('.')
        .filter(Boolean);
    if (labels.length < 2) return '';
    let tldLabels = 1;
    const last = labels[labels.length - 1];
    const secondLast = labels[labels.length - 2];
    if (labels.length >= 3 && last.length === 2 && SECOND_LEVEL_TLDS.has(secondLast)) {
        tldLabels = 2;
    }
    return labels[labels.length - tldLabels - 1] || '';
}

/**
 * Best-effort company name from an email address, or `''` when the mailbox
 * is a personal provider or the address is malformed.
 */
export function deriveCompanyFromEmail(email) {
    const text = String(email || '').trim();
    const at = text.lastIndexOf('@');
    if (at < 0) return '';
    const label = organizationLabelFromDomain(text.slice(at + 1));
    if (!label || PERSONAL_EMAIL_PROVIDERS.has(label)) return '';
    return titleCase(label.replace(/[-_]+/g, ' '));
}
